import { HttpError } from 'react-admin';

import { execAdmin } from '@src/apis/reware/utils/exec';
import type {
  FetchInventoryConstantsQuery,
  FetchInventoryItemsQuery,
  FetchInventoryItemsQueryVariables,
  FetchPartnerInventoryOptionsQuery,
  FetchPartnerInventoryOptionsQueryVariables,
} from '@src/types/__generated/rewareAdminTypes';

import type {
  ConstantOption,
  FetchInventoryItemsVariables,
  InventoryConstants,
  InventoryItemRecord,
  InventoryItemsPage,
} from './types';

// graphql-codegen extracts documents from `gql`-tagged literals; String.raw is
// the package's tag of choice (see src/resalePricing/conditionDiscounts).
const gql = String.raw;

// Cloudinary resize args for thumbnails (same convention as reware-admin)
const THUMBNAIL = { height: 300, width: 300, quality: 80, crop: 'fit' };

/**
 * The option lists behind every constants-backed filter share this shape.
 * A named fragment (rather than an interpolated string) so graphql-codegen can
 * extract the documents below.
 */
const CONSTANT_OPTION_FRAGMENT = gql`
  fragment ConstantOptionFields on ConstantsDataAdmin {
    code
    displayName
  }
`;

/**
 * Partner-scoped version of the admin `inventoryItems` query used by the
 * reware-admin Inventory Items portal. The row selection mirrors the admin
 * portal's so the expanded item view has full field parity.
 */
export const fetchInventoryItems = async (
  variables: FetchInventoryItemsVariables
): Promise<InventoryItemsPage> => {
  const query = gql`
    query fetchInventoryItems(
      $partnerId: Int
      $page: Int
      $pageSize: Int
      $sort: String
      $state: String
      $condition: String
      $sku: String
      $parentSku: String
      $brand: String
      $itemId: Int
      $searchQuery: String
      $uuid: String
      $troveSerialNumber: String
      $upc: String
      $partnerSku: String
      $title: String
      $size: String
      $color: String
      $season: String
      $department: String
      $category: String
      $price: Int
      $rejectedReason: String
      $availability: String
      $availabilityCheckin: String
      $custodyStatus: String
      $newWithTags: BooleanFieldOptions
      $isFlagged: BooleanFieldOptions
      $needsPhotography: String
      $needsRepair: String
      $needsCleaning: String
      $repairStatus: String
      $cleaningStatus: String
      $photographyStatus: String
      $catalogId: Int
      $shipmentId: Int
      $resaleUpc: String
      $preprintedLpn: String
      $shopifyProductId: String
      $shopifyVariantId: String
      $fabric: String
      $isKeyless: BooleanFieldOptions
      $warehouseCode: String
      $locationId: Int
      $box: String
      $position: String
      $locationType: String
      $wmsStatus: String
      $height: Int
      $width: Int
      $quality: Int
      $crop: String
    ) {
      inventoryItems(
        partnerId: $partnerId
        page: $page
        pageSize: $pageSize
        sort: $sort
        state: $state
        condition: $condition
        sku: $sku
        parentSku: $parentSku
        brand: $brand
        itemId: $itemId
        searchQuery: $searchQuery
        uuid: $uuid
        troveSerialNumber: $troveSerialNumber
        upc: $upc
        partnerSku: $partnerSku
        title: $title
        size: $size
        color: $color
        season: $season
        department: $department
        category: $category
        price: $price
        rejectedReason: $rejectedReason
        availability: $availability
        availabilityCheckin: $availabilityCheckin
        custodyStatus: $custodyStatus
        newWithTags: $newWithTags
        isFlagged: $isFlagged
        needsPhotography: $needsPhotography
        needsRepair: $needsRepair
        needsCleaning: $needsCleaning
        repairStatus: $repairStatus
        cleaningStatus: $cleaningStatus
        photographyStatus: $photographyStatus
        catalogId: $catalogId
        shipmentId: $shipmentId
        resaleUpc: $resaleUpc
        preprintedLpn: $preprintedLpn
        shopifyProductId: $shopifyProductId
        shopifyVariantId: $shopifyVariantId
        fabric: $fabric
        isKeyless: $isKeyless
        warehouseCode: $warehouseCode
        locationId: $locationId
        box: $box
        position: $position
        locationType: $locationType
        wmsStatus: $wmsStatus
      ) {
        count
        page
        pageSize
        rows {
          id
          uuid
          state
          state_transition_at
          condition
          deployedCondition
          recommendedCondition
          custodyStatus
          isUnsellable
          price
          notes(omitMeasurements: ALWAYS)
          storefrontPhrase
          imageUrls
          thumbnailImageUrls: imageUrls(height: $height, width: $width, quality: $quality, crop: $crop)
          catalogImageUrls
          allCatalogImageUrls
          inventoryImageUrls
          workstationImageUrls
          sendbackImageUrls
          sendbackThumbnailImageUrls: sendbackImageUrls(height: $height, width: $width, quality: $quality, crop: $crop)
          isImageFromInventory
          authStatus
          authMethod
          photoInheritanceConditionCodes
          photoUploadPositions {
            position
            required
          }
          canBeOrdered
          canUseCatalogPhotography
          isKeyless
          isFlagged
          isSupplierSendback
          needsCleaning
          needsPhotography
          needsRepair
          newWithTags
          rejectedReason
          partnerTier
          shipmentId
          sourcePartnerId
          preprintedLpn
          troveSerialNumber
          resaleUpc {
            upc
            parentSku
          }
          measurements
          wmsStatus {
            status
            code
          }
          location {
            id
            code
            parentLocationCode
            kind
            warehouse {
              code
              purpose
            }
          }
          catalogItem {
            id
            title
            description
            brand
            sku
            parent_sku
            partnerSku
            size
            color
            display_color
            availability
            availabilityCheckin
            mitiAvailability
            upc
            rubricCode
            seasonCode
            season_display
            prop65_code
            prop65_message
            shippingSurchargeAmount
            price
            originalPrice
            usedPrice
            lastKnownRetailPrice
            lastKnownOutletPrice
            msrpPrice
            yerdlePrice
            yadjustedPrice
            yadjustedCode
            fabric {
              id
              code
              name
              content
            }
            catalogFacets {
              tag
              name
            }
            measurements {
              fields {
                field
                displayName
                units
              }
            }
          }
          stateTransitions {
            id
            createdAt
            changeType
            changeFrom
            changeTo
            reason
            note
            changeDetails
            user {
              name
            }
          }
          repairStatus
          repairJobs {
            id
            uuid
            status
            createdAt
            startedAt
            declinedAt
            completedAt
            repairJobDetails {
              id
              uuid
              type
              location
            }
          }
          cleaningStatus
          cleaningJobs {
            id
            uuid
            status
            createdAt
            startedAt
            declinedAt
            completedAt
          }
          photographyStatus
          photographyJobs {
            id
            uuid
            status
            createdAt
            startedAt
            declinedAt
            completedAt
          }
          escalations {
            id
            triggerTypeDisplay
            shipmentTypeDisplay
            reasonDisplay
            closedReason
            closedAt
            createdAt
          }
          order {
            id
            orderPlacedAt
          }
          shipment {
            id
            source
          }
          tradeinItems {
            id
            createdAt
            tradein {
              id
              tradeinType
            }
          }
          inventoryItemExternals {
            externalIdentifier
            externalSystem
            externalUrl
          }
          productVariant {
            productId
            subdomain
            handle
          }
        }
      }
    }
  `;

  const data = await execAdmin<FetchInventoryItemsQueryVariables, FetchInventoryItemsQuery>(
    query,
    { variables: { ...variables, ...THUMBNAIL } }
  );

  // `inventoryItems` and its `rows` are nullable in the schema; an empty page is
  // the only sensible reading of a null here, and it keeps the list rendering.
  return {
    count: data.inventoryItems?.count ?? 0,
    page: data.inventoryItems?.page ?? 1,
    pageSize: data.inventoryItems?.pageSize ?? 0,
    rows: (data.inventoryItems?.rows ?? []).filter((row): row is InventoryItemRecord => row != null),
  };
};

type PartnerInventoryOptions = Pick<
  InventoryConstants,
  'conditions' | 'warehouses' | 'departments' | 'categories'
>;

// The generated option type has nullable code/displayName (ConstantsDataAdmin);
// drop entries missing either, since `code` becomes a SelectInput option id.
const toConstantOptions = (
  options: ReadonlyArray<{ code?: string | null, displayName?: string | null } | null> | null | undefined
): ConstantOption[] =>
  (options ?? []).flatMap(option =>
    (option?.code && option.displayName ? [{ code: option.code, displayName: option.displayName }] : []));

// Department/category filters match catalog_facets.name, so code and label are both the name.
const facetOptions = (
  facets: FetchPartnerInventoryOptionsQuery['facets'],
  tag: string
): ConstantOption[] =>
  (facets?.find(facet => facet?.tag === tag)?.values ?? [])
    .flatMap(value => (value?.name ? [{ code: value.name, displayName: value.name }] : []));

/**
 * The selected partner's own condition grades, warehouses, and department /
 * category facet values. Mirrors reware-admin's partner.constants + facets
 * lookups, scoped to the partner.
 *
 * Resolves to null when the lookup gave us nothing usable — including a
 * successful response with `partner: null`, which is what an unknown or
 * not-visible partner returns. That case has to count as a failure rather than
 * as "this partner has no warehouses", or the caller would present empty
 * dropdowns as the real option set.
 */
const fetchPartnerInventoryOptions = async (partnerId: number): Promise<PartnerInventoryOptions | null> => {
  const query = gql`
    query fetchPartnerInventoryOptions($partnerId: Int, $tags: [String]) {
      partner(id: $partnerId) {
        constants {
          inventoryItem {
            conditions { ...ConstantOptionFields }
          }
        }
        warehouses {
          code
          name
        }
      }
      facets(partnerId: $partnerId, tags: $tags) {
        tag
        values {
          name
        }
      }
    }
    ${CONSTANT_OPTION_FRAGMENT}
  `;

  const data = await execAdmin<
    FetchPartnerInventoryOptionsQueryVariables,
    FetchPartnerInventoryOptionsQuery
  >(query, { variables: { partnerId, tags: ['department', 'category'] } });

  if (data.partner == null) return null;

  return {
    conditions: toConstantOptions(data.partner?.constants?.inventoryItem?.conditions),
    warehouses: (data.partner?.warehouses ?? []).flatMap(warehouse => (warehouse?.code ? [{
      code: warehouse.code,
      displayName: warehouse.name ? `${warehouse.name} (${warehouse.code})` : warehouse.code,
    }] : [])),
    departments: facetOptions(data.facets, 'department'),
    categories: facetOptions(data.facets, 'category'),
  };
};

/**
 * Option lists for the inventory filters and item dialogs. Application-wide
 * constants are required; the partner-scoped lookup is best-effort — if it
 * fails, conditions fall back to the global set and the partner-only filters
 * (warehouse, department, category) have no options.
 *
 * `isPartial` is true whenever the result is degraded in that way, so callers
 * can tell the user rather than presenting an incomplete option set as the
 * real one. Note that `conditions` also feeds the grade-change dialog in
 * ItemActions, so a partner-scoped list narrows what an operator can set, not
 * just what they can filter by — matching reware-admin's behaviour.
 */
export const fetchInventoryConstants = async (
  partnerId: number,
): Promise<InventoryConstants & { isPartial: boolean }> => {
  const query = gql`
    query fetchInventoryConstants {
      application {
        constants {
          inventoryItem {
            states { ...ConstantOptionFields }
            allConditions { ...ConstantOptionFields }
            rejectReasons { ...ConstantOptionFields }
            inventoryItemWmsStatuses { ...ConstantOptionFields }
          }
          catalogItem {
            availabilities { ...ConstantOptionFields }
            checkinAvailabilities { ...ConstantOptionFields }
          }
          location {
            types { ...ConstantOptionFields }
          }
        }
      }
    }
    ${CONSTANT_OPTION_FRAGMENT}
  `;

  const [data, partnerOptions] = await Promise.all([
    execAdmin<Record<string, never>, FetchInventoryConstantsQuery>(query, { variables: {} }),
    fetchPartnerInventoryOptions(partnerId).catch((error: unknown) => {
      // Only a partner-scoped lookup failure is tolerable here. An auth failure
      // must propagate: these calls don't go through the data provider, so
      // react-admin's authProvider.checkError never sees them, and swallowing a
      // 401 shows a "filter options unavailable" banner to a user whose session
      // has actually expired.
      if (error instanceof HttpError && (error.status === 401 || error.status === 403)) throw error;
      console.error('Partner inventory filter options unavailable', { partnerId, error });
      return null;
    }),
  ]);

  // Every level of application.constants is nullable in the schema.
  const constants = data.application?.constants;
  const allConditions = toConstantOptions(constants?.inventoryItem?.allConditions);
  // A partner with no configured grades falls back to the global list, which
  // offers grades they don't use — filtering by one returns nothing. That's a
  // partial result too, not a clean load.
  const usePartnerConditions = !!partnerOptions?.conditions.length;
  return {
    isPartial: partnerOptions == null || !usePartnerConditions,
    states: toConstantOptions(constants?.inventoryItem?.states),
    conditions: usePartnerConditions ? partnerOptions.conditions : allConditions,
    rejectReasons: toConstantOptions(constants?.inventoryItem?.rejectReasons),
    availabilities: toConstantOptions(constants?.catalogItem?.availabilities),
    checkinAvailabilities: toConstantOptions(constants?.catalogItem?.checkinAvailabilities),
    locationTypes: toConstantOptions(constants?.location?.types),
    wmsStatuses: toConstantOptions(constants?.inventoryItem?.inventoryItemWmsStatuses),
    warehouses: partnerOptions?.warehouses ?? [],
    departments: partnerOptions?.departments ?? [],
    categories: partnerOptions?.categories ?? [],
  };
};
